import { defineStore } from 'pinia'
import { Channel } from 'pusher-js'
import { markRaw, ref, watch } from 'vue'

import { useQueryClient } from '@/composables/vue-query'
import {
  BrandTypeId,
  type ItineraryWaypointItem,
  type ItineraryWaypointPlaceItem,
  type ItineraryDayItem,
  type ItineraryItem,
  type ItineraryPlaceItem,
  type ItineraryDayPlaceItem,
  isWaypointItem,
  isDayItem,
} from '@/types/itinerary'
import type {
  DateRange,
  ItineraryItem as GroupedItineraryItem,
  ItineraryTrip,
} from '@/types/trip'
import { tripQueryKeys, type useTripItinerary } from '@/composables/trip'

export const useItineraryStore = defineStore('itinerary', () => {
  const queryClient = useQueryClient()

  /**
   * Internal reference to the basic trip data.
   */
  const trip = ref<ItineraryTrip>()

  /**
   * This contains the raw items from our itinerary v3 endpoint.
   *
   * These are grouped by waypoint.
   */
  const groupedItems = ref<GroupedItineraryItem[]>([])

  /**
   * This is our single source of truth for the itinerary.
   *
   * It contains all child elements of the trip, like waypoints, pois and
   * days. The flat structure makes it easier to work with in the UI.
   */
  const items = ref<ItineraryItem[]>([])

  /**
   * A map for easy parent lookups in the flat itinerary item list.
   */
  const itemParentMap = ref<
    Record<string, ItineraryWaypointItem | ItineraryDayItem>
  >({})

  /**
   * Whether to show the itinerary.
   */
  const showItinerary = ref(false)

  const tripUpdateChannel = ref<Channel | null>(null)

  /**
   * Subscribe to trip updates via Pusher. Ensure you also unsubscribe correctly.
   *
   * @param tripId - The trip ID to subscribe to.
   */
  function subscribeToTripUpdates(tripId: string) {
    if (process.server) {
      return
    }

    unsubscribeFromTripUpdates()

    const { $pusher } = self.$nuxt
    const channel = $pusher.subscribe(`private-${tripId}`)
    channel.bind(
      'trip/updated',
      (data: {
        preview: { name?: string; startDate?: DateRange; endDate?: DateRange }
      }) => {
        if (trip.value) {
          if (data.preview.name) {
            trip.value.name = data.preview.name
          }
          if (data.preview.startDate) {
            trip.value.startDate = data.preview.startDate
          }
          if (data.preview.endDate) {
            trip.value.endDate = data.preview.endDate
          }
          queryClient.invalidateQueries({
            queryKey: tripQueryKeys.itinerary(trip.value.id),
          })
        }
      },
    )
    tripUpdateChannel.value = markRaw(channel)
  }

  /**
   * Unsubscribe from trip updates.
   */
  function unsubscribeFromTripUpdates() {
    if (tripUpdateChannel.value) {
      tripUpdateChannel.value.unsubscribe()
      tripUpdateChannel.value = null
    }
  }

  /**
   * Initialize the itinerary store from a trip itinerary query. This will watch
   * the query and update the store when the query data changes.
   *
   * @param query - The trip itinerary query.
   */
  function initializeFromQuery(query: ReturnType<typeof useTripItinerary>) {
    watch(
      () => query.data.value,
      (newData) => {
        trip.value = newData?.trip
        groupedItems.value = newData?.itinerary ?? []
        itemParentMap.value = {}
        items.value = []
        for (const { waypoint } of groupedItems.value) {
          const waypointItem: ItineraryWaypointItem = {
            type: 'waypoint',
            id: `waypoint_${waypoint._id}`,
            parent: 'trip',
            waypointId: waypoint._id,
            name: waypoint.name,
            picture: waypoint.picture,
            startDate: waypoint.startDate,
            endDate: waypoint.endDate,
            summary: waypoint.summary,
            [BrandTypeId]: 'Waypoint',
          }
          items.value.push(waypointItem)

          for (const item of waypoint.itemsWithoutDate) {
            if (item.type === 'place' && typeof item._id === 'string') {
              const waypointPlaceItem: ItineraryWaypointPlaceItem = {
                ...item,
                type: 'place',
                id: `waypoint_${waypoint._id}/place_${item._id}`,
                parent: 'waypoint',
                waypointId: waypoint._id,
                poiId: item._id,
                [BrandTypeId]: 'WaypointPlace',
              }
              items.value.push(waypointPlaceItem)
              itemParentMap.value[waypointPlaceItem.id] = waypointItem
            }
          }

          for (const day of waypoint.days) {
            const dayItem: ItineraryDayItem = {
              type: 'day',
              id: `waypoint_${waypoint._id}/day_${day._id}`,
              parent: 'waypoint',
              date: day.date,
              dayId: day._id,
              waypointId: waypoint._id,
              [BrandTypeId]: 'Day',
            }
            items.value.push(dayItem)
            itemParentMap.value[dayItem.id] = waypointItem

            for (const item of day.items) {
              if (item.type === 'place' && typeof item._id === 'string') {
                const dayPlaceItem: ItineraryDayPlaceItem = {
                  ...item,
                  id: `day_${day._id}/place_${item.itemId}`,
                  type: 'place',
                  parent: 'day',
                  dayId: day._id,
                  waypointId: waypoint._id,
                  itemId: item.itemId,
                  [BrandTypeId]: 'DayPlace',
                }
                items.value.push(dayPlaceItem)
                itemParentMap.value[dayPlaceItem.id] = dayItem
              }
            }
          }
        }
      },
      { immediate: true },
    )
  }

  /**
   * Get the parent item of a given itinerary item.
   *
   * @param item - The itinerary item.
   * @returns The parent item.
   */
  function getParentItem(item: ItineraryItem) {
    return itemParentMap.value[item.id]
  }

  /**
   * Get the children of a given itinerary item.
   *
   * @param parent - The itinerary item.
   * @returns The children.
   */
  function getChildren(parent: ItineraryItem) {
    if (isWaypointItem(parent)) {
      const item = groupedItems.value.find(
        (i) => i.waypoint._id === parent.waypointId,
      )
      return item?.waypoint.itemsWithoutDate ?? []
    }

    if (isDayItem(parent)) {
      const item = groupedItems.value.find(
        (i) => i.waypoint._id === parent.waypointId,
      )
      if (!item) {
        return []
      }

      return item.waypoint.days.find((d) => d._id === parent.dayId)?.items ?? []
    }

    return []
  }

  /**
   * Find the index of a given itinerary place item in its parent.
   *
   * @param targetItem - The itinerary place item.
   * @returns The index.
   */
  function findItemIndexInParent(targetItem: ItineraryPlaceItem) {
    const item = groupedItems.value.find(
      (i) => i.waypoint._id === targetItem.waypointId,
    )
    if (!item) {
      return -1
    }

    const { waypoint } = item
    if (targetItem.parent === 'day') {
      const day = waypoint.days.find((d) => d._id === targetItem.dayId)
      if (!day) {
        return -1
      }

      return day.items.findIndex((i) => i.itemId === targetItem.itemId)
    }

    return -1
  }

  return {
    trip,
    groupedItems,
    items,
    itemParentMap,
    initializeFromQuery,
    getParentItem,
    getChildren,
    findItemIndexInParent,
    showItinerary,
    subscribeToTripUpdates,
    unsubscribeFromTripUpdates,
  }
})
